import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';

import { PropsWithChildren, createContext, useMemo } from 'react';

import { getAccountOptions } from '@lib/api/options/auth';
import { Route } from '@lib/types';
import { findMatchingRoute } from '@lib/utils/route';
import {
  getAllModulesRoutes,
  getModuleRoutesByAccount,
  isPublicRoutePath,
} from '@modules/index';

interface InternalRouteCTXProps {
  activeRoute: Route | null;
  accountBasedRoutes: Route[];
  allRoutes: Route[];
}

export const InternalRouteCTX = createContext<InternalRouteCTXProps>({
  activeRoute: null,
  accountBasedRoutes: [],
  allRoutes: [],
});

const InternalRouteProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();

  const isPublicPath = isPublicRoutePath(location.pathname);

  const { data: account } = useQuery({
    ...getAccountOptions(),
    enabled: !isPublicPath,
  });

  const accountBasedRoutes = getModuleRoutesByAccount(account);

  const normalizedPath =
    location.pathname === '/'
      ? '/'
      : location.pathname.replace(/\/+$/, '') || '/';

  const activeRoute = useMemo(
    () => findMatchingRoute(getAllModulesRoutes(), normalizedPath) ?? null,
    [normalizedPath],
  );

  const allRoutes = getAllModulesRoutes();

  const value = useMemo(
    () => ({ activeRoute, accountBasedRoutes, allRoutes }),
    [activeRoute, accountBasedRoutes, allRoutes],
  );

  return (
    <InternalRouteCTX.Provider value={value}>
      {children}
    </InternalRouteCTX.Provider>
  );
};

export default InternalRouteProvider;
